import { Injectable, signal } from '@angular/core';
import { CryptoBundle } from './models';

/**
 * Where the unwrapped DEK is cached so a page refresh doesn't force a re-unlock.
 * sessionStorage survives a reload within the same tab but is cleared when the
 * tab/browser is closed, so the vault still locks when the user actually leaves.
 */
const DEK_SESSION_KEY = 'pk_dek';

/**
 * Zero-knowledge cryptography. Everything here runs in the browser using the
 * Web Crypto API. The master password and the Data Encryption Key (DEK) never
 * leave the device. The server only ever receives ciphertext and wrapped keys.
 *
 *  - KEK (Key Encryption Key): derived from the master password via PBKDF2.
 *  - DEK (Data Encryption Key): random AES-GCM key that encrypts vault data.
 *    The DEK is wrapped (encrypted) by the KEK before being sent to the server.
 */
@Injectable({ providedIn: 'root' })
export class CryptoService {
  static readonly DEFAULT_ITERATIONS = 600_000;
  static readonly ALGORITHM = 'PBKDF2-SHA256';

  /** The unwrapped DEK, held only in memory while the vault is unlocked. */
  private dek: CryptoKey | null = null;
  readonly isUnlocked = signal(false);

  get unlocked(): boolean {
    return this.dek !== null;
  }

  lock(): void {
    this.dek = null;
    this.isUnlocked.set(false);
    sessionStorage.removeItem(DEK_SESSION_KEY);
  }

  /**
   * Re-import the DEK cached in sessionStorage after a page refresh. Returns
   * false when there is nothing cached (or it is unusable), meaning the user
   * must unlock with their master password again.
   */
  async restore(): Promise<boolean> {
    if (this.dek) return true;
    const cached = sessionStorage.getItem(DEK_SESSION_KEY);
    if (!cached) return false;
    try {
      const rawDek = this.fromB64(cached);
      this.dek = await crypto.subtle.importKey('raw', this.ab(rawDek), { name: 'AES-GCM' }, false, [
        'encrypt',
        'decrypt'
      ]);
      this.isUnlocked.set(true);
      return true;
    } catch {
      sessionStorage.removeItem(DEK_SESSION_KEY);
      this.dek = null;
      this.isUnlocked.set(false);
      return false;
    }
  }

  /** Registration: create a fresh DEK and wrap it with a KEK from the master password. */
  async createVaultKeys(masterPassword: string): Promise<CryptoBundle> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iterations = CryptoService.DEFAULT_ITERATIONS;
    const kek = await this.deriveKek(masterPassword, salt, iterations);

    const dek = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
      'encrypt',
      'decrypt'
    ]);
    const rawDek = await crypto.subtle.exportKey('raw', dek);

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const wrapped = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: this.ab(iv) }, kek, rawDek);

    return {
      kdfSalt: this.toB64(salt),
      kdfIterations: iterations,
      kdfAlgorithm: CryptoService.ALGORITHM,
      wrappedDek: this.toB64(new Uint8Array(wrapped)),
      wrappedDekIv: this.toB64(iv)
    };
  }

  /** Unlock: derive the KEK from the master password and unwrap the DEK. */
  async unlock(masterPassword: string, bundle: CryptoBundle): Promise<boolean> {
    try {
      const salt = this.fromB64(bundle.kdfSalt);
      const kek = await this.deriveKek(masterPassword, salt, bundle.kdfIterations);
      const iv = this.fromB64(bundle.wrappedDekIv);
      const wrapped = this.fromB64(bundle.wrappedDek);

      const rawDek = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: this.ab(iv) },
        kek,
        this.ab(wrapped)
      );
      this.dek = await crypto.subtle.importKey('raw', rawDek, { name: 'AES-GCM' }, false, [
        'encrypt',
        'decrypt'
      ]);
      // Cache the unwrapped DEK so a page refresh doesn't force a re-unlock.
      sessionStorage.setItem(DEK_SESSION_KEY, this.toB64(new Uint8Array(rawDek)));
      this.isUnlocked.set(true);
      return true;
    } catch {
      this.dek = null;
      this.isUnlocked.set(false);
      return false; // wrong master password (GCM auth fails)
    }
  }

  /**
   * Master password change: unwrap the DEK with the current master password,
   * then re-wrap it under a KEK derived from the new one (fresh salt + IV).
   * The DEK itself is unchanged, so existing vault data stays readable.
   * Returns null when the current master password is wrong.
   */
  async rewrapDek(
    currentMasterPassword: string,
    newMasterPassword: string,
    bundle: CryptoBundle
  ): Promise<CryptoBundle | null> {
    let rawDek: ArrayBuffer;
    try {
      const salt = this.fromB64(bundle.kdfSalt);
      const kek = await this.deriveKek(currentMasterPassword, salt, bundle.kdfIterations);
      rawDek = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: this.ab(this.fromB64(bundle.wrappedDekIv)) },
        kek,
        this.ab(this.fromB64(bundle.wrappedDek))
      );
    } catch {
      return null; // wrong current master password (GCM auth fails)
    }

    const newSalt = crypto.getRandomValues(new Uint8Array(16));
    const iterations = CryptoService.DEFAULT_ITERATIONS;
    const newKek = await this.deriveKek(newMasterPassword, newSalt, iterations);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const wrapped = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: this.ab(iv) }, newKek, rawDek);

    return {
      kdfSalt: this.toB64(newSalt),
      kdfIterations: iterations,
      kdfAlgorithm: CryptoService.ALGORITHM,
      wrappedDek: this.toB64(new Uint8Array(wrapped)),
      wrappedDekIv: this.toB64(iv)
    };
  }

  /** Encrypt an arbitrary string with the DEK. */
  async encrypt(plaintext: string): Promise<{ iv: string; ciphertext: string }> {
    this.ensureUnlocked();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = new TextEncoder().encode(plaintext);
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: this.ab(iv) }, this.dek!, this.ab(data));
    return { iv: this.toB64(iv), ciphertext: this.toB64(new Uint8Array(ct)) };
  }

  /** Decrypt a string previously produced by encrypt(). */
  async decrypt(ivB64: string, ciphertextB64: string): Promise<string> {
    this.ensureUnlocked();
    const iv = this.fromB64(ivB64);
    const ct = this.fromB64(ciphertextB64);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: this.ab(iv) }, this.dek!, this.ab(ct));
    return new TextDecoder().decode(plain);
  }

  private async deriveKek(
    masterPassword: string,
    salt: Uint8Array,
    iterations: number
  ): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      this.ab(new TextEncoder().encode(masterPassword)),
      'PBKDF2',
      false,
      ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt: this.ab(salt), iterations, hash: 'SHA-256' },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private ensureUnlocked(): void {
    if (!this.dek) {
      throw new Error('Vault is locked.');
    }
  }

  /** Copy a view's bytes into a standalone ArrayBuffer (satisfies BufferSource typing). */
  private ab(view: Uint8Array | ArrayBuffer): ArrayBuffer {
    if (view instanceof ArrayBuffer) return view;
    const out = new ArrayBuffer(view.byteLength);
    new Uint8Array(out).set(view);
    return out;
  }

  private toB64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
  }

  private fromB64(b64: string): Uint8Array {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
